import {
  ColDef,
  GridOptions,
  themeAlpine,
  ValueFormatterParams,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { memo, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import { IgGrayPrimitives } from "@intelligems/ig-design-tokens/constants";
import { percentNoDecimalFormatter } from "@intelligems/ig-graphs/data-formatters";
import { AnalyzeTinybirdResourceResponseBodyType } from "@intelligems/ig-types/functions/analytics/analyze-resource-tinybird/analyze-resource-tinybird";
import { GraphConfig } from "@intelligems/ig-types/schemas/analytics/custom-graphs";
import { Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { useConfiguration } from "@src/api/configurations/use-configuration";
import {
  BaseStatSigHeaderTooltip,
  GroupCellRenderer,
  StatSigCellInterval,
  StatSigIntervalHeader,
  UpliftCellRenderer,
  ValueCellRenderer,
} from "@src/features/analytics/metricDetails/statSig/StatSigComponentHelpers";
import { dataFormatWrapper } from "@src/features/analytics/metricDetails/statSig/utils";
import { getExperienceAnalyticsFilters } from "@src/store/slices/dashboardFilters";
import { selectedVariations } from "@src/store/slices/variations";
import { theme } from "@src/theme";
import { StatSigData } from "@src/types/analytics-tinybird";
import { sortByOrder } from "@src/utils/sortings";
import { UseQueryResult } from "@tanstack/react-query";

const myTheme = themeAlpine.withParams({
  fontFamily: theme.typography.fontFamily,
  rowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerRowBorder: `1px solid ${IgGrayPrimitives[600]}`,
  headerFontWeight: 600,
  headerTextColor: IgGrayPrimitives["A300"],
  headerFontSize: "14px",
  wrapperBorder: `0px solid ${IgGrayPrimitives[600]}`,
});

const StyledGrid = styled(Grid)(({ theme }) => ({
  // [`& .ag-header-cell:last-child:after`]: {
  //   backgroundColor: "transparent",
  // },
  "& ::-webkit-scrollbar": {
    width: 0 /* Remove scrollbar space */,
    background: "transparent" /* Optional: just make scrollbar invisible */,
  },
  [`& .ag-root-wrapper`]: {
    // border: "solid 1px  #ECECEC !important",
    borderBottomLeftRadius: "10px",
    borderBottomRightRadius: "10px",
    fontSize: "14px",
  },
  // header bottom border color
  [`& .ag-header`]: {
    // borderBottom: "solid 1px #ECECEC !important",
    textAlign: "center",
  },
  [`& .ag-row:last-child`]: {
    borderBottom: "solid 0px !important",
  },
  [`& .ag-header-cell-label`]: {
    fontWeight: 400,
    fontSize: "14px",
  },
  [`& .ag-cell-wrap-text`]: {
    wordBreak: "break-word",
  },
  [`& .ag-header-cell-norm`]: {
    [`&.ag-header-cell`]: {
      backgroundColor: "white !important",
      borderRight: `1px solid ${theme.igPrimitives.gray[600]} !important`,
    },
  },
  [`& .ag-header-cell-end`]: {
    [`&.ag-header-cell`]: {
      backgroundColor: "white !important",
      borderRight: "0px solid transparent !important",
    },
  },
  [`& .ag-header-cell-text`]: {
    color: `${theme.igPrimitives.gray["A300"]} !important`,
    fontWeight: 600,
    fontSize: "14px",
  },
  [`& .ag-aria-description-container`]: {
    position: "absolute !important",
    top: "-9999px !important",
    left: "-9999px !important",
    height: "1px !important",
    width: "1px !important",
    overflow: "hidden !important",
  },
  [`& .ag-cell-norm`]: {
    [`&.ag-cell`]: {
      fontSize: "16px !important",
      color: theme.palette.text.secondary,
      borderRightColor: `${theme.igPrimitives.gray[600]} !important`,
      alignItems: "center",
      display: "flex",
      textAlign: "center",
      justifyContent: "center",
    },
  },
  [`& .ag-cell-end`]: {
    [`&.ag-cell`]: {
      fontSize: "16px !important",
      color: theme.palette.text.secondary,
    },
  },
  [`& .ag-center-cols-viewport`]: {
    minHeight: "120px !important",
  },
}));

const BaseSkeleton = () => {
  return (
    <Skeleton
      variant="rectangular"
      width={31}
      height={15}
      sx={{ borderRadius: "5px" }}
    />
  );
};

interface StatSigSectionProps {
  variationOverviewData: UseQueryResult<AnalyzeTinybirdResourceResponseBodyType>[];
  selectedMetricId: string;
  graphConfigs: GraphConfig[];
}

const StatSigSectionComponent = ({
  variationOverviewData,
  selectedMetricId,
  graphConfigs,
}: StatSigSectionProps) => {
  const storeCurrencyConfig = useConfiguration<{
    code: string;
    locale: string;
  }>("shop_currency");
  const selectedConfig = useMemo(() => {
    return (
      graphConfigs.find(
        (c) =>
          c.impactMetric === selectedMetricId ||
          c.metrics[0] === selectedMetricId
      ) || graphConfigs[0]
    );
  }, [selectedMetricId]);
  const gridRef = useRef<AgGridReact>(null);
  const variations = useSelector(selectedVariations);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);

  const selectedBaseline = variations.find(
    (v) => v.id === dashboardFilters.compareGroupA
  );
  const selectedBaselineId = selectedBaseline?.id ?? "";
  const defaultColDef = useMemo<ColDef>(() => {
    return {
      flex: 1,
      minWidth: 90,
      resizable: false,
      cellStyle: { fontSize: "12px", alignContent: "center" },
      menuTabs: [],
      suppressMovable: true,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      suppressHeaderMenuButton: true,
      sortable: false,
    };
  }, []);

  const selectedVariationOverviewData: StatSigData[] = useMemo(() => {
    if (variationOverviewData && variationOverviewData[0]?.data?.metrics) {
      const formattedData = variationOverviewData[0].data.metrics.map(
        (data) => ({
          value: dataFormatWrapper(
            data[selectedMetricId]?.value || 0,
            selectedConfig.dataTypes[0],
            storeCurrencyConfig!.value
          ),
          uplift: {
            ci_range:
              !data[selectedMetricId]?.uplift?.ci_low ||
              !data[selectedMetricId]?.uplift?.ci_high
                ? undefined
                : [
                    data[selectedMetricId]?.uplift?.ci_low,
                    data[selectedMetricId]?.uplift?.ci_high,
                  ],
            value: data[selectedMetricId]?.uplift?.value,
            plus_minus: data[selectedMetricId]?.uplift?.plus_minus,
          },
          plus_minus: data[selectedMetricId]?.plus_minus,
          p2bb: data[selectedMetricId]?.p2bb,
          p2bc: data[selectedMetricId]?.p2bc,
          ci_range:
            !data[selectedMetricId]?.ci_low || !data[selectedMetricId]?.ci_high
              ? undefined
              : [
                  dataFormatWrapper(
                    data[selectedMetricId]?.ci_low,
                    selectedConfig.dataTypes[0],
                    storeCurrencyConfig!.value
                  ),
                  dataFormatWrapper(
                    data[selectedMetricId]?.ci_high,
                    selectedConfig.dataTypes[0],
                    storeCurrencyConfig!.value
                  ),
                ],
          variation_id: data.variation_id,
          order:
            variations.find((variation) => variation.id === data.variation_id)
              ?.order ?? 0,
        })
      );
      // .sort(sortByOrder);

      variations.forEach((variation) => {
        if (!formattedData.some((data) => data.variation_id === variation.id)) {
          formattedData.push({
            value: 0,
            uplift: {
              ci_range: undefined,
              value: null,
              plus_minus: null,
            },
            plus_minus: null,
            p2bb: null,
            p2bc: null,
            ci_range: undefined,
            variation_id: variation.id,
            order: variation.order,
          });
        }
      });
      return formattedData.sort(sortByOrder);
    } else {
      return variations
        .map((variation) => ({
          value: 2,
          uplift: {
            ci_range: [0, 100],
            value: 0,
            plus_minus: 0,
          },
          plus_minus: 0,
          p2bb: 0,
          p2bc: 0,
          ci_range: [0, 2],
          variation_id: variation.id,
          order: variation.order,
        }))
        .sort(sortByOrder);
    }
  }, [variationOverviewData, selectedMetricId, selectedConfig]);

  const gridOptions = useMemo<GridOptions>(() => {
    return {
      domLayout: "autoHeight",
    };
  }, []);

  const hasProbabilities = useMemo(() => {
    const foundConfig = graphConfigs.find((config) =>
      config.impactMetric
        ? config.impactMetric === selectedMetricId
        : config.metrics[0] === selectedMetricId
    );
    return foundConfig?.hasProbability || false;
  }, [selectedMetricId]);

  const hasValueCi = useMemo(() => {
    const foundConfig = graphConfigs.find((config) =>
      config.impactMetric
        ? config.impactMetric === selectedMetricId
        : config.metrics[0] === selectedMetricId
    );
    return foundConfig?.hasValueCI || false;
  }, [selectedMetricId]);

  const columnDefs: ColDef[] = useMemo(() => {
    return [
      {
        headerName: "Group",
        field: "variation_id",
        flex: 0.3,
        cellClass: "ag-cell-norm",
        cellStyle: {
          justifyContent: "flex-start",
        },
        headerClass: "ag-header-cell-norm",
        cellRendererParams: { variations },
        cellRenderer: GroupCellRenderer,
      },
      {
        headerName: "Uplift",
        headerClass: "ag-header-cell-norm",
        headerComponent: BaseStatSigHeaderTooltip,
        headerComponentParams: { tooltipKey: "uplift" },
        field: "uplift.value",
        flex: 0.45,
        cellClass: "ag-cell-norm",
        cellRendererParams: {
          baselineVariationId: selectedBaseline?.id || "",
          storeCurrencyConfig: storeCurrencyConfig!.value,
          isLoading:
            variationOverviewData[0].isLoading ||
            variationOverviewData[0].isPending,
          selectedConfig,
        },
        cellRenderer: UpliftCellRenderer,
      },
      {
        headerName: "Probability to Beat Control",
        headerClass: "ag-header-cell-norm",
        field: "p2bc",
        flex: 0.25,
        cellClass: "ag-cell-norm",
        cellRenderer:
          variationOverviewData[0].isLoading ||
          variationOverviewData[0].isPending
            ? BaseSkeleton
            : ValueCellRenderer,
        headerComponent: BaseStatSigHeaderTooltip,
        headerComponentParams: { tooltipKey: "p2bc" },
        cellRendererParams: {
          isP2bc: true,
          baselineVariationId: selectedBaselineId,
        },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params?.data["variation_id"] === selectedBaselineId) {
            return "n/a";
          }
          if (
            !hasProbabilities ||
            params.value === undefined ||
            params.value === null
          ) {
            return "";
          }
          return percentNoDecimalFormatter(
            params.value,
            true,
            storeCurrencyConfig!.value
          );
        },
      },
      {
        headerName: "Probability to Be Best",
        headerClass: "ag-header-cell-norm",
        field: "p2bb",
        flex: 0.25,
        cellClass: "ag-cell-norm",
        cellRendererParams: {
          isP2bc: false,
          baselineVariationId: selectedBaselineId,
        },
        cellRenderer:
          variationOverviewData[0].isLoading ||
          variationOverviewData[0].isPending
            ? BaseSkeleton
            : ValueCellRenderer,
        headerComponent: BaseStatSigHeaderTooltip,
        headerComponentParams: { tooltipKey: "p2bb" },
        valueFormatter: (params: ValueFormatterParams) => {
          if (params.value === undefined || params.value === null) {
            return "";
          }
          return percentNoDecimalFormatter(
            params.value,
            true,
            storeCurrencyConfig!.value
          );
        },
      },
      {
        headerName: "interval",
        headerClass: "ag-header-cell-end",
        field: "graph-header",
        cellClass: "ag-cell-end",
        headerComponent: StatSigIntervalHeader,
        headerComponentParams: {
          statSigData: selectedVariationOverviewData,
          config: selectedConfig,
          isLoading: variationOverviewData[0].isLoading,
          hasValueCI: hasValueCi,
        },
        cellRendererParams: {
          config: selectedConfig,
          statSigData: selectedVariationOverviewData,
          isLoading:
            variationOverviewData[0].isLoading ||
            variationOverviewData[0].isPending,
          hasValueCI: hasValueCi,
        },
        cellRenderer: StatSigCellInterval,
      },
    ];
  }, [
    variationOverviewData[0].isLoading,
    variationOverviewData[0].isPending,
    selectedVariationOverviewData,
    hasProbabilities,
    hasValueCi,
    selectedConfig,
  ]);

  return (
    <StyledGrid
      container
      direction={"column"}
      rowSpacing={2}
      sx={{ marginTop: "0px" }}
    >
      <AgGridReact
        theme={myTheme}
        ref={gridRef}
        defaultColDef={defaultColDef}
        rowData={selectedVariationOverviewData}
        columnDefs={columnDefs}
        groupDisplayType={"groupRows"}
        overlayNoRowsTemplate={
          "<div>No Data - Your filters may have resulted in no data, try resetting them</div>"
        }
        gridOptions={gridOptions}
        suppressDragLeaveHidesColumns={true}
        suppressExcelExport={true}
        suppressCsvExport={true}
        suppressContextMenu={true}
        suppressCellFocus={true}
        suppressRowHoverHighlight={true}
        rowHeight={60}
      />
    </StyledGrid>
  );
};

// have to do it like this because the component has too many nested functions
export const StatSigSection = memo(StatSigSectionComponent);
StatSigSection.displayName = "StatSigSection";
